import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";

import ParanaNetworkMap from "./ParanaNetworkMap";
import AdminConsole from "./admin/AdminConsole";
import DebugGlpiId from "./DebugGlpiId";
import Dashboard from "./Dashboard";

const resolveApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  return envUrl ? String(envUrl).replace(/\/$/, "") : "";
};

const API_BASE = resolveApiBase();

const LOGIN_REDIRECT_URL = "/Login/?next=%2F";

function RedirectToLogin() {
  const [count, setCount] = useState(3);
  const [progress, setProgress] = useState(0);
  const [redirecting, setRedirecting] = useState(false);

  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  const manualRedirect = () => {
    if (redirecting) return;
    setRedirecting(true);
    // Pequeno delay só para o usuário ver o feedback visual.
    setTimeout(() => {
      window.location.replace(LOGIN_REDIRECT_URL);
    }, 250);
  };

  useEffect(() => {
    const totalDuration = 3000;
    const intervalStep = 50;
    let elapsedTime = 0;
    let lastSecond = 3;

    const timer = setInterval(() => {
      elapsedTime += intervalStep;

      const percentage = Math.min((elapsedTime / totalDuration) * 100, 100);
      setProgress(percentage);

      const remainingSeconds = Math.max(
        0,
        Math.ceil((totalDuration - elapsedTime) / 1000)
      );
      if (remainingSeconds !== lastSecond) {
        lastSecond = remainingSeconds;
        setCount(remainingSeconds);
      }

      if (elapsedTime >= totalDuration) {
        clearInterval(timer);
        manualRedirect();
      }
    }, intervalStep);

    return () => clearInterval(timer);
  }, [redirecting]);

  // Canvas particles (fundo)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let particles = [];

    const particleCount = 60;
    const connectionDistance = 140;
    const mouseDistance = 200;
    const mouse = { x: null, y: null };

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    const onMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.6;
        this.vy = (Math.random() - 0.5) * 0.6;
        this.size = Math.random() * 2 + 1;
        this.alpha = Math.random() * 0.5 + 0.2;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;

        if (mouse.x != null && mouse.y != null) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance > 0 && distance < mouseDistance) {
            const forceDirectionX = dx / distance;
            const forceDirectionY = dy / distance;
            const force = (mouseDistance - distance) / mouseDistance;
            const directionX = forceDirectionX * force * 0.8;
            const directionY = forceDirectionY * force * 0.8;
            this.x -= directionX;
            this.y -= directionY;
          }
        }
      }

      draw() {
        ctx.fillStyle = `rgba(239, 68, 68, ${this.alpha})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];
        p1.update();
        p1.draw();

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            ctx.beginPath();
            const opacity = 1 - distance / connectionDistance;
            ctx.strokeStyle = `rgba(239, 68, 68, ${opacity * 0.15})`;
            ctx.lineWidth = 1;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }

      rafRef.current = window.requestAnimationFrame(animate);
    };

    resize();
    initParticles();
    animate();

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouseMove);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const buttonContent = useMemo(() => {
    if (redirecting) {
      return (
        <>
          <span className="lrp-btn-icon">✓</span>
          Redirecionado!
        </>
      );
    }

    return (
      <>
        <span className="lrp-btn-icon">→</span>
        Acessar Login Agora
      </>
    );
  }, [redirecting]);

  return (
    <div className="login-redirect-page">
      <canvas ref={canvasRef} className="login-redirect-canvas" />
      <div className="login-redirect-overlay" />
      <div className="login-redirect-glow login-redirect-glow-1" />
      <div className="login-redirect-glow login-redirect-glow-2" />

      <div className="login-redirect-content">
        <div className="login-redirect-card lrp-animate-in">
          <div className="login-redirect-loader">
            <div className="login-redirect-loader-glow" />
            <div className="loader-ring" aria-label="Carregando">
              <div />
              <div />
              <div />
              <div />
            </div>
          </div>

          <h1 className="login-redirect-title">Redirecionando</h1>
          <p className="login-redirect-subtitle">
            Aguarde enquanto preparamos seu acesso...
          </p>

          <div className="login-redirect-progress">
            <div
              className="login-redirect-progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>

          <p className="login-redirect-countdown">
            Redirecionando em{" "}
            <span className="login-redirect-countdown-value">{count}</span>s
          </p>

          <button
            type="button"
            className={
              redirecting
                ? "login-redirect-btn login-redirect-btn-ok"
                : "login-redirect-btn"
            }
            onClick={manualRedirect}
          >
            {buttonContent}
          </button>

          <div className="login-redirect-footer">
            <span className="login-redirect-footer-icon">🔒</span> Conexão Segura
            e Criptografada
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [authStatus, setAuthStatus] = useState("loading");

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/";

  // 🔒 Login é servido fora do React (Vite build estático)
  // React NÃO interfere nele
  const isLoginRoute = pathname.startsWith("/Login");

  useEffect(() => {
    if (isLoginRoute) {
      // Não checa auth em /Login
      return;
    }

    const checkAuth = async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          credentials: "include",
        });

        if (res.ok) {
          setAuthStatus("ok");
          return;
        }
      } catch (_) {}

      setAuthStatus("fail");
    };

    checkAuth();
  }, [isLoginRoute]);

  // Em /Login o React não renderiza nada
  if (isLoginRoute) {
    return null;
  }

  if (authStatus === "loading") {
    return null;
  }

  if (authStatus === "fail") {
    return <RedirectToLogin />;
  }

  // ===== ROTAS PROTEGIDAS =====
  if (pathname === "/glpi_id") return <DebugGlpiId />;
  if (pathname === "/dashboard") return <Dashboard />;
  if (pathname === "/admin") return <AdminConsole apiBaseUrl={API_BASE} />;

  return <ParanaNetworkMap />;
}

export default App;
